import { useState, useEffect } from "react";
import { Plus, FileText, Trash2, Receipt, Download, Gauge, Mail, MessageCircle, Barcode, UserCheck, Edit, Search, Link, Send, Clock, Wrench, AlertTriangle, X, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import CustomerVehicleSelector from "../components/CustomerVehicleSelector";
import { generateQuotePDF } from "../utils/pdfGenerator";
import { archivePdf } from "../utils/archiveService";
import TemparioSelector from "../components/TemparioSelector";
import { autoPartsDescriptions } from "../data/autoParts";
import CityAutocomplete from "../components/CityAutocomplete";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quotes({ user }) {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Share quote with public PDF link
  const [shareData, setShareData] = useState(null); // { quote, url }
  const [sharing, setSharing] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Parts Request creation dialog
  const [showPartsRequestDialog, setShowPartsRequestDialog] = useState(false);
  const [newQuoteData, setNewQuoteData] = useState(null);
  const [partsRequestConfig, setPartsRequestConfig] = useState({
    supplier_id: "",
    supplier_name: "",
    supplier_email: "",
    supplier_phone: "",
    urgency: "standard"
  });
  
  // Acceptances for importing customer/vehicle data
  const [acceptances, setAcceptances] = useState([]);
  const [selectedAcceptanceId, setSelectedAcceptanceId] = useState("");

  const [formData, setFormData] = useState({
    // Tipo cliente
    customer_type: "",  // B2C, B2B, PA - obbligatorio
    customer_id: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    customer_city: "",
    customer_province: "",
    customer_cap: "",
    customer_fiscal_code: "",
    customer_vat_number: "",
    customer_sdi_code: "",
    customer_pec: "",
    // Campi PA
    cig: "",
    cup: "",
    split_payment: false,
    // Veicolo
    vehicle_id: "",
    vehicle_plate: "",
    vehicle_brand: "",
    vehicle_model: "",
    vehicle_version: "",
    vehicle_year: "",
    vehicle_vin: "",
    vehicle_engine_code: "",
    vehicle_km: "",
    vehicle_fuel_type: "",
    vehicle_cubic_capacity: "",
    vehicle_power_kw: "",
    vehicle_power_hp: "",
    vehicle_first_registration: "",
    // Articoli con tipi
    items: [{ 
      line_type: "RICAMBIO",  // RICAMBIO, MANODOPERA, SERVIZIO, PRATICA_ASSICURATIVA, SPESE_ACCESSORIE
      item_code: "", 
      oem_code: "",
      description: "", 
      brand: "", 
      quantity: "1", 
      unit_price: "", 
      cost_price: "",  // Costo acquisto per calcolo margine
      vat_rate: "22",
      natura: "",  // N1, N2.x, etc per IVA 0%
      discount_percent: "",
      // Campi manodopera
      hours: "",
      hourly_rate: "",
      intervention_type: ""
    }],
    labor_cost: "",
    labor_hours: "",
    waste_disposal_cost: "",
    consumables_cost: "",
    document_type: "TD01",  // TD01, TD04, TD24
    notes: "",
    validity_days: "",
    payment_method: "MP05"
  });
  
  // Store ore manodopera suggerite dal tempario per ogni item
  const [suggestedLaborHours, setSuggestedLaborHours] = useState({});
  
  // Store default settings from officina configuration
  const [defaultSettings, setDefaultSettings] = useState({
    default_labor_cost: 0,
    default_waste_disposal_cost: 0,
    default_consumables_cost: 0
  });

  // Work Order creation dialog (on quote acceptance)
  const [showWorkOrderDialog, setShowWorkOrderDialog] = useState(false);
  const [pendingAcceptQuote, setPendingAcceptQuote] = useState(null);
  const [workOrderConfig, setWorkOrderConfig] = useState({
    urgency: "medium",
    bridge_number: 1,
    operator_id: "",
    notes: ""
  });
  const [workOrderOperators, setWorkOrderOperators] = useState([]);
  const [workOrderBridges, setWorkOrderBridges] = useState(1);
  const [creatingWorkOrder, setCreatingWorkOrder] = useState(false);

  // Province italiane per dropdown
  const PROVINCE_ITALIANE = [
    "AG", "AL", "AN", "AO", "AP", "AQ", "AR", "AT", "AV", "BA", "BG", "BI", "BL", "BN", "BO", 
    "BR", "BS", "BT", "BZ", "CA", "CB", "CE", "CH", "CL", "CN", "CO", "CR", "CS", "CT", "CZ",
    "EN", "FC", "FE", "FG", "FI", "FM", "FR", "GE", "GO", "GR", "IM", "IS", "KR", "LC", "LE",
    "LI", "LO", "LT", "LU", "MB", "MC", "ME", "MI", "MN", "MO", "MS", "MT", "NA", "NO", "NU",
    "OR", "PA", "PC", "PD", "PE", "PG", "PI", "PN", "PO", "PR", "PT", "PU", "PV", "PZ", "RA",
    "RC", "RE", "RG", "RI", "RM", "RN", "RO", "SA", "SI", "SO", "SP", "SR", "SS", "SU", "SV",
    "TA", "TE", "TN", "TO", "TP", "TR", "TS", "TV", "UD", "VA", "VB", "VC", "VE", "VI", "VR", "VT", "VV"
  ];

  // Tipi cliente
  const CUSTOMER_TYPES = {
    B2C: { label: "Privato", color: "text-blue-400", bgColor: "bg-blue-400/10" },
    B2B: { label: "Azienda", color: "text-green-400", bgColor: "bg-green-400/10" },
    PA: { label: "Pubblica Amministrazione", color: "text-amber-400", bgColor: "bg-amber-400/10" }
  };

  // Tipi riga automotive
  const LINE_TYPES = {
    RICAMBIO: { label: "Ricambio", icon: "🔧", color: "text-zinc-300" },
    MANODOPERA: { label: "Manodopera", icon: "👷", color: "text-blue-400" },
    SERVIZIO: { label: "Servizio", icon: "🛠️", color: "text-purple-400" },
    PRATICA_ASSICURATIVA: { label: "Pratica Assicurativa", icon: "📋", color: "text-orange-400" },
    SPESE_ACCESSORIE: { label: "Spese Accessorie", icon: "📦", color: "text-gray-400" }
  };

  // Nature IVA per aliquota 0% - Include tutti i codici Reverse Charge N6.x
  const NATURE_IVA = [
    { code: "", label: "-- Seleziona Natura --" },
    // Escluse e Non soggette
    { code: "N1", label: "N1 - Escluse ex art.15 DPR 633/72" },
    { code: "N2.1", label: "N2.1 - Non soggette artt. 7-7septies" },
    { code: "N2.2", label: "N2.2 - Non soggette altri casi" },
    // Non imponibili
    { code: "N3.1", label: "N3.1 - Non imponibili esportazioni" },
    { code: "N3.2", label: "N3.2 - Non imponibili cessioni intraUE" },
    { code: "N3.3", label: "N3.3 - Non imponibili cessioni San Marino" },
    { code: "N3.4", label: "N3.4 - Non imponibili operazioni assimilate" },
    { code: "N3.5", label: "N3.5 - Non imponibili dichiarazione intento" },
    { code: "N3.6", label: "N3.6 - Non imponibili altre operazioni" },
    // Esenti e Regime margine
    { code: "N4", label: "N4 - Esenti art.10" },
    { code: "N5", label: "N5 - Regime del margine / IVA non esposta" },
    // REVERSE CHARGE - Inversione contabile (tutti i codici N6.x)
    { code: "N6.1", label: "N6.1 - RC Rottami e materiali recupero" },
    { code: "N6.2", label: "N6.2 - RC Oro e argento puro" },
    { code: "N6.3", label: "N6.3 - RC Subappalto edilizia" },
    { code: "N6.4", label: "N6.4 - RC Cessione fabbricati" },
    { code: "N6.5", label: "N6.5 - RC Telefoni cellulari" },
    { code: "N6.6", label: "N6.6 - RC Prodotti elettronici" },
    { code: "N6.7", label: "N6.7 - RC Prestazioni edili/connessi" },
    { code: "N6.8", label: "N6.8 - RC Settore energetico" },
    { code: "N6.9", label: "N6.9 - RC Altri casi art.17 c.6" },
    // IVA assolta in UE
    { code: "N7", label: "N7 - IVA assolta in altro Stato UE" }
  ];

  // Tipi documento
  const DOCUMENT_TYPES = [
    { code: "TD01", label: "TD01 - Fattura" },
    { code: "TD04", label: "TD04 - Nota di Credito" },
    { code: "TD24", label: "TD24 - Fattura Differita" }
  ];

  // Payment methods for FatturaPA
  const paymentMethods = [
    { code: "MP01", label: "Contanti" },
    { code: "MP02", label: "Assegno" },
    { code: "MP03", label: "Assegno circolare" },
    { code: "MP05", label: "Bonifico" },
    { code: "MP08", label: "Carta di pagamento" },
    { code: "MP12", label: "RIBA" },
    { code: "MP14", label: "Quietanza erario" },
    { code: "MP15", label: "Giroconto su conti enti" },
    { code: "MP16", label: "Domiciliazione bancaria" },
    { code: "MP17", label: "Domiciliazione postale" },
    { code: "MP18", label: "Bollettino postale" },
    { code: "MP19", label: "SEPA DD" },
    { code: "MP20", label: "SEPA DD CORE" },
    { code: "MP21", label: "SEPA DD B2B" },
    { code: "MP22", label: "Trattenuta su somme già riscosse" },
    { code: "MP23", label: "PagoPA" }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Filter quotes based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredQuotes(quotes);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = quotes.filter(q => 
        q.quote_number?.toLowerCase().includes(term) ||
        q.customer_name?.toLowerCase().includes(term) ||
        q.vehicle_plate?.toLowerCase().includes(term) ||
        q.vehicle_brand?.toLowerCase().includes(term) ||
        q.vehicle_model?.toLowerCase().includes(term)
      );
      setFilteredQuotes(filtered);
    }
  }, [searchTerm, quotes]);

  const fetchData = async () => {
    try {
      const [quotesRes, customersRes, invoicesRes, suppliersRes, acceptancesRes, settingsRes] = await Promise.all([
        authFetch(`${API}/quotes`, { }),
        authFetch(`${API}/customers`, { }),
        authFetch(`${API}/invoices`, { }),
        authFetch(`${API}/suppliers`, { }),
        authFetch(`${API}/acceptances`, { }),
        authFetch(`${API}/officina/settings`, { })
      ]);
      if (quotesRes.ok) {
        const data = await quotesRes.json();
        setQuotes(data);
        setFilteredQuotes(data);
      }
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
      if (suppliersRes.ok) setSuppliers(await suppliersRes.json());
      if (acceptancesRes.ok) setAcceptances(await acceptancesRes.json());
      if (settingsRes.ok) {
        const settings = await settingsRes.json();
        setDefaultSettings({
          default_labor_cost: settings.default_labor_cost || 0,
          default_waste_disposal_cost: settings.default_waste_disposal_cost || 0,
          default_consumables_cost: settings.default_consumables_cost || 0
        });
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  // Get invoice number for a quote
  const getLinkedInvoice = (quoteNumber) => {
    const invoice = invoices.find(inv => inv.quote_number === quoteNumber);
    return invoice?.invoice_number || null;
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer_type: customer.customer_type || "",
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        customer_address: customer.address,
        customer_city: customer.city || "",
        customer_province: customer.province || "",
        customer_cap: customer.zip_code || "",
        customer_fiscal_code: customer.fiscal_code,
        customer_vat_number: customer.vat_number || "",
        customer_sdi_code: customer.sdi_code || "",
        customer_pec: customer.pec || "",
        cig: customer.cig || "",
        cup: customer.cup || "",
        split_payment: customer.split_payment || (customer.customer_type === "PA")
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione tipo cliente obbligatorio
    if (!formData.customer_type) {
      toast.error("Seleziona il tipo di cliente (Privato, Azienda o PA)");
      return;
    }
    
    // Convert string values to numbers before sending
    const dataToSend = {
      ...formData,
      customer_id: selectedCustomerId || null,
      // Nuovi campi B2C/B2B/PA
      customer_type: formData.customer_type,
      cig: formData.cig || "",
      cup: formData.cup || "",
      split_payment: formData.customer_type === "PA" ? true : formData.split_payment,
      document_type: formData.document_type || "TD01",
      // Conversioni numeriche
      vehicle_km: parseInt(formData.vehicle_km) || 0,
      vehicle_year: parseInt(formData.vehicle_year) || 0,
      labor_cost: parseFloat(formData.labor_cost) || 0,
      labor_hours: parseFloat(formData.labor_hours) || 0,
      waste_disposal_cost: parseFloat(formData.waste_disposal_cost) || 0,
      consumables_cost: parseFloat(formData.consumables_cost) || 0,
      validity_days: parseInt(formData.validity_days) || 30,
      items: formData.items.map(item => ({
        ...item,
        line_type: item.line_type || "RICAMBIO",
        quantity: parseFloat(item.quantity) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        cost_price: parseFloat(item.cost_price) || 0,  // Costo acquisto per margini
        vat_rate: parseFloat(item.vat_rate) || 22,
        natura: item.vat_rate === "0" || item.vat_rate === 0 ? (item.natura || "") : "",
        discount_percent: parseFloat(item.discount_percent) || 0,
        hours: parseFloat(item.hours) || 0,
        hourly_rate: parseFloat(item.hourly_rate) || 0
      }))
    };

    const isEditing = editingQuote !== null;
    
    // If creating new quote, show parts request dialog first
    if (!isEditing && dataToSend.items.length > 0) {
      setNewQuoteData(dataToSend);
      setShowDialog(false);
      setShowPartsRequestDialog(true);
      return;
    }

    // Otherwise proceed with save
    await saveQuote(dataToSend, isEditing);
  };

  const saveQuote = async (dataToSend, isEditing, partsConfig = null) => {
    try {
      // Add parts request config if provided
      if (partsConfig) {
        dataToSend = {
          ...dataToSend,
          create_parts_request: partsConfig.createRequest,
          parts_request_supplier_id: partsConfig.supplier_id || null,
          parts_request_supplier_name: partsConfig.supplier_name || "",
          parts_request_supplier_email: partsConfig.supplier_email || "",
          parts_request_supplier_phone: partsConfig.supplier_phone || "",
          parts_request_urgency: partsConfig.urgency || "standard"
        };
      } else {
        dataToSend.create_parts_request = false;
      }

      const url = isEditing ? `${API}/quotes/${editingQuote.quote_id}` : `${API}/quotes`;
      const method = isEditing ? "PUT" : "POST";

      const response = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        const result = await response.json();
        if (isEditing) {
          toast.success(`Preventivo #${result.quote_number} modificato`);
        } else {
          let msg = `Preventivo #${result.quote_number} creato.`;
          if (result.points_earned > 0) msg += ` Punti guadagnati: ${result.points_earned}`;
          if (partsConfig?.createRequest) msg += ` Richiesta ricambi creata.`;
          toast.success(msg);
        }
        setShowDialog(false);
        setShowPartsRequestDialog(false);
        setNewQuoteData(null);
        setEditingQuote(null);
        resetForm();
        setPartsRequestConfig({
          supplier_id: "",
          supplier_name: "",
          supplier_email: "",
          supplier_phone: "",
          urgency: "standard"
        });
        fetchData();
      } else {
        throw new Error("Errore");
      }
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handlePartsRequestConfirm = (createRequest) => {
    if (!newQuoteData) return;
    
    const partsConfig = createRequest ? {
      createRequest: true,
      supplier_id: partsRequestConfig.supplier_id,
      supplier_name: partsRequestConfig.supplier_name,
      supplier_email: partsRequestConfig.supplier_email,
      supplier_phone: partsRequestConfig.supplier_phone,
      urgency: partsRequestConfig.urgency
    } : { createRequest: false };

    saveQuote(newQuoteData, false, partsConfig);
  };

  const handleSupplierSelect = (supplierId) => {
    const supplier = suppliers.find(s => s.supplier_id === supplierId);
    if (supplier) {
      setPartsRequestConfig(prev => ({
        ...prev,
        supplier_id: supplierId,
        supplier_name: supplier.name,
        supplier_email: supplier.email || "",
        supplier_phone: supplier.phone || ""
      }));
    } else {
      setPartsRequestConfig(prev => ({
        ...prev,
        supplier_id: "",
        supplier_name: "",
        supplier_email: "",
        supplier_phone: ""
      }));
    }
  };

  const openEditDialog = (quote) => {
    setEditingQuote(quote);
    setSelectedCustomerId(quote.customer_id || "");
    setFormData({
      customer_id: quote.customer_id || "",
      customer_name: quote.customer_name || "",
      customer_email: quote.customer_email || "",
      customer_phone: quote.customer_phone || "",
      customer_address: quote.customer_address || "",
      customer_city: quote.customer_city || "",
      customer_province: quote.customer_province || "",
      customer_cap: quote.customer_cap || "",
      customer_fiscal_code: quote.customer_fiscal_code || "",
      customer_vat_number: quote.customer_vat_number || "",
      customer_sdi_code: quote.customer_sdi_code || "",
      customer_pec: quote.customer_pec || "",
      vehicle_id: quote.vehicle_id || "",
      vehicle_plate: quote.vehicle_plate || "",
      vehicle_brand: quote.vehicle_brand || "",
      vehicle_model: quote.vehicle_model || "",
      vehicle_version: quote.vehicle_version || "",
      vehicle_year: quote.vehicle_year || "",
      vehicle_vin: quote.vehicle_vin || "",
      vehicle_engine_code: quote.vehicle_engine_code || "",
      vehicle_km: quote.vehicle_km || "",
      vehicle_fuel_type: quote.vehicle_fuel_type || "",
      vehicle_cubic_capacity: quote.vehicle_cubic_capacity || "",
      vehicle_power_kw: quote.vehicle_power_kw || "",
      vehicle_power_hp: quote.vehicle_power_hp || "",
      vehicle_first_registration: quote.vehicle_first_registration || "",
      items: quote.items?.length > 0 ? quote.items : [{ item_code: "", description: "", brand: "", quantity: "", unit_price: "", vat_rate: "" }],
      labor_cost: quote.labor_cost || "",
      labor_hours: quote.labor_hours || "",
      waste_disposal_cost: quote.waste_disposal_cost || "",
      consumables_cost: quote.consumables_cost || "",
      notes: quote.notes || "",
      validity_days: quote.validity_days || "",
      payment_method: quote.payment_method || "MP05"
    });
    setShowDialog(true);
  };

  const handleStatusChange = async (quoteId, status) => {
    // If changing to "accepted", show work order dialog first
    if (status === "accepted") {
      const quote = quotes.find(q => q.quote_id === quoteId) || selectedQuote;
      if (quote) {
        setPendingAcceptQuote({ quoteId, quote });
        // Load operators and bridges from settings
        try {
          const res = await authFetch(`${API}/officina/settings`, { });
          if (res.ok) {
            const settings = await res.json();
            setWorkOrderOperators(settings.operators || []);
            setWorkOrderBridges(settings.num_bridges || 1);
          }
        } catch (error) {
          console.error("Error loading settings:", error);
        }
        setShowWorkOrderDialog(true);
        return;
      }
    }
    
    // Normal status change for other statuses
    await executeStatusChange(quoteId, status);
  };

  const executeStatusChange = async (quoteId, status) => {
    try {
      const response = await authFetch(`${API}/quotes/${quoteId}/status?status=${status}`, {
        method: "PATCH"
      });

      if (response.ok) {
        toast.success("Stato aggiornato");
        fetchData();
        if (selectedQuote) {
          setSelectedQuote({...selectedQuote, status});
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Create work order when quote is accepted
  const handleCreateWorkOrder = async () => {
    if (!pendingAcceptQuote) return;
    
    setCreatingWorkOrder(true);
    
    try {
      // First update quote status
      await executeStatusChange(pendingAcceptQuote.quoteId, "accepted");
      
      // Then create work order
      const response = await authFetch(`${API}/work-orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quote_id: pendingAcceptQuote.quoteId,
          urgency: workOrderConfig.urgency,
          bridge_number: workOrderConfig.bridge_number,
          operator_id: workOrderConfig.operator_id || null,
          notes: workOrderConfig.notes
        })
      });

      if (response.ok) {
        const workOrder = await response.json();
        toast.success(`Ordine di lavoro #${workOrder.work_order_number} creato`);
        setShowWorkOrderDialog(false);
        setPendingAcceptQuote(null);
        setWorkOrderConfig({ urgency: "medium", bridge_number: 1, operator_id: "", notes: "" });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nella creazione dell'ordine di lavoro");
      }
    } catch (error) {
      toast.error("Errore nella creazione dell'ordine di lavoro");
    } finally {
      setCreatingWorkOrder(false);
    }
  };

  // Skip work order creation
  const handleSkipWorkOrder = async () => {
    if (!pendingAcceptQuote) return;
    
    await executeStatusChange(pendingAcceptQuote.quoteId, "accepted");
    setShowWorkOrderDialog(false);
    setPendingAcceptQuote(null);
  };

  // State per dialog validazione fattura
  const [invoiceValidation, setInvoiceValidation] = useState(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  // Verifica validazione prima di creare fattura
  const validateAndCreateInvoice = async (quoteId) => {
    try {
      // Prima verifica se il preventivo è pronto per la fatturazione
      const validationRes = await authFetch(`${API}/invoices/validate-quote/${quoteId}`, {
      });
      
      if (validationRes.ok) {
        const validation = await validationRes.json();
        
        if (validation.ready && (!validation.format_errors || validation.format_errors.length === 0)) {
          // Tutto ok, nessun warning - procedi direttamente
          createInvoice(quoteId);
        } else if (validation.ready && validation.format_errors && validation.format_errors.length > 0) {
          // Ready ma con warning di formato - mostra dialog con opzione di procedere
          setInvoiceValidation({ ...validation, canProceed: true });
          setShowValidationDialog(true);
        } else {
          // Errori bloccanti - mostra dialog
          setInvoiceValidation({ ...validation, canProceed: false });
          setShowValidationDialog(true);
        }
      } else {
        // Errore nella validazione, prova comunque
        createInvoice(quoteId);
      }
    } catch (error) {
      // In caso di errore, prova comunque (il backend validerà)
      createInvoice(quoteId);
    }
  };

  const createInvoice = async (quoteId) => {
    try {
      const response = await authFetch(`${API}/invoices/from-quote/${quoteId}`, {
        method: "POST"
      });

      if (response.ok) {
        toast.success("Fattura creata con successo");
        setShowValidationDialog(false);
        setInvoiceValidation(null);
        navigate("/invoices");
      } else {
        const error = await response.json();
        // Formatta il messaggio di errore per una migliore leggibilità
        const errorMessage = error.detail || "Errore nella creazione fattura";
        if (errorMessage.includes("\n")) {
          toast.error(
            <div className="text-sm">
              <p className="font-semibold mb-1">Impossibile creare fattura:</p>
              {errorMessage.split("\n").map((line, i) => (
                <p key={i} className="text-xs">{line}</p>
              ))}
            </div>,
            { duration: 8000 }
          );
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error) {
      toast.error("Errore nella creazione fattura");
    }
  };

  // Professional PDF Generation using new pdfGenerator
  const generatePDFBlob = (quote) => {
    const doc = generateQuotePDF(quote, user);
    return doc.output('blob');
  };

  const downloadPDF = (quote) => {
    const doc = generateQuotePDF(quote, user);
    doc.save(`Preventivo_${quote.quote_number}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF scaricato");
  };

  // Delete quote with number reassignment
  const handleDeleteQuote = async (quoteId) => {
    try {
      const response = await authFetch(`${API}/quotes/${quoteId}`, {
        method: "DELETE"
      });
      if (response.ok) {
        toast.success("Preventivo eliminato");
        setSelectedQuote(null);
        setDeleteConfirm(null);
        fetchData();
      } else {
        throw new Error("Errore");
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const sendViaEmail = async (quote) => {
    const subject = encodeURIComponent(`Preventivo #${quote.quote_number} - PServicePro`);
    const body = encodeURIComponent(
`Gentile ${quote.customer_name},

Le inviamo il preventivo n. ${quote.quote_number} per il veicolo ${quote.vehicle_model} (${quote.vehicle_plate}).

RIEPILOGO:
- Veicolo: ${quote.vehicle_model} - ${quote.vehicle_plate}
- Km: ${(quote.vehicle_km || 0).toLocaleString()}
- Imponibile: € ${(quote.subtotal || 0).toFixed(2)}
- IVA: € ${(quote.vat_total || 0).toFixed(2)}
- TOTALE: € ${(quote.total || 0).toFixed(2)}

Validità: ${quote.validity_days || 30} giorni dalla data di emissione.

Per qualsiasi informazione, non esiti a contattarci.

Cordiali saluti,
PServicePro - Gestione Officina`
    );
    
    // Open email client
    window.open(`mailto:${quote.customer_email}?subject=${subject}&body=${body}`, '_blank');
    toast.success("Client email aperto");
  };

  const sendViaWhatsApp = (quote) => {
    // Clean phone number (remove spaces, dashes, etc.)
    let phone = (quote.customer_phone || '').replace(/[\s\-\(\)\.]/g, '');
    // Add Italy prefix if not present
    if (!phone.startsWith('+') && !phone.startsWith('39')) {
      phone = '39' + phone;
    } else if (phone.startsWith('+')) {
      phone = phone.substring(1);
    }
    
    const message = encodeURIComponent(
`🔧 *PServicePro - Preventivo #${quote.quote_number}*

Gentile *${quote.customer_name}*,

Le inviamo il preventivo per il suo veicolo:
🚗 *${quote.vehicle_model}* - ${quote.vehicle_plate}
📍 Km: ${(quote.vehicle_km || 0).toLocaleString()}

📋 *DETTAGLIO LAVORI:*
${(quote.items || []).map(item => `• ${item.description} (${item.quantity}x) - € ${(item.subtotal || (item.quantity * item.unit_price * 1.22) || 0).toFixed(2)}`).join('\n')}
${quote.labor_hours > 0 ? `• Manodopera (${quote.labor_hours}h) - € ${((quote.labor_cost || 0) * (quote.labor_hours || 0)).toFixed(2)}` : ''}

💰 *TOTALE: € ${(quote.total || 0).toFixed(2)}* (IVA inclusa)

⏰ Validità: ${quote.validity_days || 30} giorni

Per confermare o per info, risponda a questo messaggio.

_PServicePro - Gestione Officina_`
    );
    
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    toast.success("WhatsApp aperto");
  };

  // Generate a public shareable PDF link and open the share dialog
  const openShareDialog = async (quote) => {
    setSharing(true);
    try {
      const res = await authFetch(`${API}/quotes/${quote.quote_id}/share`, { method: "POST" });
      if (res.ok) {
        const { token } = await res.json();
        const url = `${window.location.origin}/preventivo/${token}`;
        setShareData({ quote, url });
        setLinkCopied(false);
        // Archivia copia immutabile del PDF inviato (in background)
        archivePdf(generatePDFBlob(quote), "quote", quote.quote_id, quote.quote_number);
      } else {
        toast.error("Errore nella generazione del link");
      }
    } catch (e) {
      toast.error("Errore nella generazione del link");
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    if (!shareData) return;
    try {
      await navigator.clipboard.writeText(shareData.url);
      setLinkCopied(true);
      toast.success("Link copiato");
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (e) {
      toast.error("Impossibile copiare il link");
    }
  };

  const shareViaWhatsApp = () => {
    if (!shareData) return;
    const { quote, url } = shareData;
    let phone = (quote.customer_phone || '').replace(/[\s\-\(\)\.]/g, '');
    if (!phone.startsWith('+') && !phone.startsWith('39')) {
      phone = '39' + phone;
    } else if (phone.startsWith('+')) {
      phone = phone.substring(1);
    }
    const message = encodeURIComponent(
`🔧 *Preventivo #${quote.quote_number}*

Gentile *${quote.customer_name}*,
in allegato il preventivo per il suo veicolo ${quote.vehicle_plate}.

💰 *TOTALE: € ${(quote.total || 0).toFixed(2)}* (IVA inclusa)

📄 Visualizza e scarica il PDF qui:
${url}

⏰ Validità: ${quote.validity_days || 30} giorni

_PServicePro - Gestione Officina_`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
    toast.success("WhatsApp aperto");
  };

  const shareViaEmail = () => {
    if (!shareData) return;
    const { quote, url } = shareData;
    const subject = encodeURIComponent(`Preventivo #${quote.quote_number} - PServicePro`);
    const body = encodeURIComponent(
`Gentile ${quote.customer_name},

Le inviamo il preventivo n. ${quote.quote_number} per il veicolo ${quote.vehicle_plate}.

TOTALE: € ${(quote.total || 0).toFixed(2)} (IVA inclusa)

Può visualizzare e scaricare il PDF del preventivo da questo link:
${url}

Validità: ${quote.validity_days || 30} giorni dalla data di emissione.

Cordiali saluti,
PServicePro - Gestione Officina`
    );
    window.open(`mailto:${quote.customer_email}?subject=${subject}&body=${body}`, '_blank');
    toast.success("Client email aperto");
  };

  const resetForm = () => {
    setSelectedCustomerId("");
    setEditingQuote(null);
    setFormData({
      customer_id: "",
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      customer_address: "",
      customer_city: "",
      customer_province: "",
      customer_cap: "",
      customer_fiscal_code: "",
      customer_vat_number: "",
      customer_sdi_code: "",
      customer_pec: "",
      vehicle_id: "",
      vehicle_plate: "",
      vehicle_brand: "",
      vehicle_model: "",
      vehicle_version: "",
      vehicle_year: "",
      vehicle_vin: "",
      vehicle_engine_code: "",
      vehicle_km: "",
      vehicle_fuel_type: "",
      vehicle_cubic_capacity: "",
      vehicle_power_kw: "",
      vehicle_power_hp: "",
      vehicle_first_registration: "",
      items: [{ item_code: "", description: "", brand: "", quantity: "1", unit_price: "", cost_price: "", vat_rate: "22" }],
      labor_cost: defaultSettings.default_labor_cost || "",
      labor_hours: "",
      waste_disposal_cost: defaultSettings.default_waste_disposal_cost || "",
      consumables_cost: defaultSettings.default_consumables_cost || "",
      notes: "",
      validity_days: "",
      payment_method: "MP05"
    });
    setSelectedAcceptanceId("");
  };

  const addItem = (lineType = "RICAMBIO") => {
    const newItem = {
      line_type: lineType,
      item_code: "",
      oem_code: "",
      description: "",
      brand: "",
      quantity: "1",
      unit_price: "",
      cost_price: "",
      vat_rate: "22",
      natura: "",
      discount_percent: "",
      intervention_type: ""
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => {
        const itemToRemove = prev.items[index];
        
        // Filtra l'item e la sua riga manodopera associata (se esiste)
        const newItems = prev.items.filter((item, i) => {
          // Rimuovi l'item selezionato
          if (i === index) return false;
          // Rimuovi anche la riga manodopera associata (parent_index === index)
          if (item.is_labor && item.parent_index === index) return false;
          return true;
        });
        
        // Aggiorna parent_index per le righe manodopera dopo l'item rimosso
        const updatedItems = newItems.map(item => {
          if (item.is_labor && item.parent_index > index) {
            return { ...item, parent_index: item.parent_index - 1 };
          }
          return item;
        });
        
        // Ricalcola le ore manodopera totali dalle righe rimanenti
        const newLaborHours = updatedItems
          .filter(item => item.is_labor || item.line_type === "MANODOPERA")
          .reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
        
        // Arrotonda a 2 decimali
        const roundedLaborHours = Math.round(newLaborHours * 100) / 100;
        
        return {
          ...prev,
          items: updatedItems,
          labor_hours: roundedLaborHours.toString()
        };
      });
      
      // Aggiorna anche suggestedLaborHours rimuovendo l'indice
      setSuggestedLaborHours(prev => {
        const newSuggested = {};
        Object.keys(prev).forEach(key => {
          const keyNum = parseInt(key);
          if (keyNum < index) {
            newSuggested[key] = prev[key];
          } else if (keyNum > index) {
            newSuggested[keyNum - 1] = prev[key];
          }
          // Salta keyNum === index (l'item rimosso)
        });
        return newSuggested;
      });
    }
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? {...item, [field]: value} : item)
    }));
  };

  // Funzione per aggiornare item con dati completi dal tempario/catalogo
  // E creare automaticamente una riga manodopera separata
  const updateItemFromCatalog = (index, catalogItem) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      
      // Aggiorna l'item selezionato - codice articolo VUOTO per inserimento manuale
      newItems[index] = {
        ...newItems[index],
        item_code: "", // Leave code empty for manual entry
        description: catalogItem.descrizione,
        unit_price: catalogItem.prezzo > 0 ? catalogItem.prezzo.toString() : newItems[index].unit_price,
        cost_price: catalogItem.costo_acquisto > 0 ? catalogItem.costo_acquisto.toString() : newItems[index].cost_price || "",
        vat_rate: catalogItem.aliquota_iva?.toString() || "22",
        quantity: newItems[index].quantity || "1",
        is_labor: false
      };

      // Se ha costo acquisto, mostra toast
      if (catalogItem.costo_acquisto > 0) {
        toast.info(`Costo acquisto: €${catalogItem.costo_acquisto.toFixed(2)}`);
      }

      // Se ha ore manodopera, crea riga manodopera e aggiorna totale
      let newLaborHours = parseFloat(prev.labor_hours) || 0;
      console.log("ore_manodopera:", catalogItem.ore_manodopera, "currentLaborHours:", newLaborHours);
      
      if (catalogItem.ore_manodopera > 0) {
        const laborCostPerHour = parseFloat(prev.labor_cost) || parseFloat(defaultSettings.default_labor_cost) || 40;
        const laborHours = catalogItem.ore_manodopera;
        
        // Aggiungi ore al totale
        newLaborHours += laborHours;
        
        // Cerca se esiste già una riga manodopera per questo item
        const laborRowIndex = newItems.findIndex((item, i) => 
          i > index && 
          item.is_labor && 
          item.parent_index === index
        );

        // Crea riga manodopera separata
        const laborRow = {
          line_type: "MANODOPERA",
          item_code: `MAN-${String(index + 1).padStart(3, '0')}`,
          description: `Manodopera: ${catalogItem.descrizione}`,
          brand: "",
          quantity: laborHours.toString(),
          unit_price: laborCostPerHour.toString(),
          vat_rate: "22",
          is_labor: true,
          parent_index: index
        };

        if (laborRowIndex > -1) {
          // Aggiorna riga esistente
          newItems[laborRowIndex] = laborRow;
        } else {
          // Inserisci nuova riga manodopera dopo l'articolo
          newItems.splice(index + 1, 0, laborRow);
        }

        toast.success(`Aggiunta manodopera: ${laborHours}h per "${catalogItem.descrizione.substring(0, 30)}..."`);
      }

      // Arrotonda a 2 decimali per evitare errori floating point (es: 1.2000000000000002)
      const roundedLaborHours = Math.round(newLaborHours * 100) / 100;

      return { 
        ...prev, 
        items: newItems,
        labor_hours: roundedLaborHours.toString()
      };
    });

    // Aggiorna le ore suggerite per il tracking
    if (catalogItem.ore_manodopera > 0) {
      setSuggestedLaborHours(prev => ({
        ...prev,
        [index]: catalogItem.ore_manodopera
      }));
    }
  };

  // Calcola totale ore manodopera dagli items
  const calculateTotalLaborHours = () => {
    return formData.items
      .filter(item => item.is_labor)
      .reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  };

  // Calcola totale ore suggerite
  const totalSuggestedHours = Object.values(suggestedLaborHours).reduce((sum, h) => sum + h, 0);

  const calculateTotals = () => {
    let subtotal = 0;
    let vat = 0;
    let totalLaborHoursFromItems = 0;
    let laborSubtotalFromItems = 0;
    let ricambiSubtotal = 0;  // Totale imponibile ricambi
    let manodoperaSubtotal = 0;  // Totale imponibile manodopera

    formData.items.forEach(item => {
      const qty = parseFloat(String(item.quantity || "").replace(',', '.')) || 0;
      const price = parseFloat(String(item.unit_price || "").replace(',', '.')) || 0;
      const vatRate = parseFloat(String(item.vat_rate || "22").replace(',', '.')) || 22;
      const itemSubtotal = qty * price;
      
      // Arrotonda per evitare errori floating point
      const roundedSubtotal = Math.round(itemSubtotal * 100) / 100;
      subtotal += roundedSubtotal;
      vat += roundedSubtotal * (vatRate / 100);
      
      // Traccia ore manodopera dalle righe
      if (item.is_labor || item.line_type === "MANODOPERA") {
        totalLaborHoursFromItems += qty;
        laborSubtotalFromItems += roundedSubtotal;
        manodoperaSubtotal += roundedSubtotal;  // Aggiungi a manodopera
      } else {
        // Se non è manodopera, è ricambio (o altro tipo che va nei ricambi)
        ricambiSubtotal += roundedSubtotal;
      }
    });

    // Manodopera dal campo labor_cost * labor_hours
    const laborCostStr = String(formData.labor_cost || "").replace(',', '.');
    const laborHoursStr = String(formData.labor_hours || "").replace(',', '.');
    const laborCost = parseFloat(laborCostStr) || 0;
    const laborHours = parseFloat(laborHoursStr) || 0;
    const laborFieldSubtotal = Math.round(laborCost * laborHours * 100) / 100;
    
    // LOGICA FATTURAZIONE ELETTRONICA:
    // Il campo labor_hours indica le ore TOTALI da fatturare
    // Se ci sono righe MANODOPERA, quelle sono già incluse nel subtotale
    // Se le righe MANODOPERA non coprono tutte le ore, aggiungi la differenza
    // Questo evita doppio conteggio ma permette flessibilità
    
    // Se NON ci sono righe manodopera, usa il campo
    if (totalLaborHoursFromItems === 0 && laborFieldSubtotal > 0) {
      subtotal += laborFieldSubtotal;
      vat += laborFieldSubtotal * 0.22;
      manodoperaSubtotal += laborFieldSubtotal;  // Aggiungi a manodopera
    }

    // Add waste disposal and consumables costs (vanno nei ricambi/servizi)
    const wasteDisposalCost = parseFloat(String(formData.waste_disposal_cost || "").replace(',', '.')) || 0;
    const consumablesCost = parseFloat(String(formData.consumables_cost || "").replace(',', '.')) || 0;
    
    // Arrotonda
    const roundedWaste = Math.round(wasteDisposalCost * 100) / 100;
    const roundedCons = Math.round(consumablesCost * 100) / 100;
    subtotal += roundedWaste + roundedCons;
    vat += (roundedWaste + roundedCons) * 0.22;
    ricambiSubtotal += roundedWaste + roundedCons;  // Smaltimento e consumabili vanno nei ricambi

    // Arrotonda totali finali
    subtotal = Math.round(subtotal * 100) / 100;
    vat = Math.round(vat * 100) / 100;
    ricambiSubtotal = Math.round(ricambiSubtotal * 100) / 100;
    manodoperaSubtotal = Math.round(manodoperaSubtotal * 100) / 100;

    return { 
      subtotal, 
      vat, 
      total: Math.round((subtotal + vat) * 100) / 100, 
      totalLaborHours: totalLaborHoursFromItems > 0 ? totalLaborHoursFromItems : laborHours,
      laborSubtotalFromItems,
      ricambiSubtotal,      // Nuovo: totale ricambi
      manodoperaSubtotal    // Nuovo: totale manodopera
    };
  };

  const statusColors = {
    draft: "status-draft",
    sent: "status-sent",
    accepted: "status-accepted",
    rejected: "status-rejected",
    expired: "status-cancelled",
    invoiced: "status-completed"
  };

  const statusLabels = {
    draft: "Bozza",
    sent: "Inviato",
    accepted: "Accettato",
    rejected: "Rifiutato",
    expired: "Scaduto",
    invoiced: "Fatturato"
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="quotes-page" className="space-y-6">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Preventivi</h1>
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-quote-btn" className="btn-primary" onClick={() => { resetForm(); setShowDialog(true); }}>
              <Plus className="w-5 h-5 mr-2" />
              Nuovo Preventivo
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] text-xl">
                {editingQuote ? `Modifica Preventivo #${editingQuote.quote_number}` : "Nuovo Preventivo"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Import from Acceptance */}
              {!editingQuote && acceptances.length > 0 && (
                <div className="form-group p-3 bg-zinc-900/50 border border-primary/30 rounded-lg">
                  <label className="form-label flex items-center gap-2 text-primary">
                    <Link className="w-4 h-4" />
                    Importa da Accettazione
                  </label>
                  <select
                    className="form-input"
                    value={selectedAcceptanceId}
                    onChange={(e) => {
                      const acceptanceId = e.target.value;
                      setSelectedAcceptanceId(acceptanceId);
                      if (acceptanceId) {
                        const acceptance = acceptances.find(a => a.acceptance_id === acceptanceId);
                        if (acceptance) {
                          setSelectedCustomerId(acceptance.customer_id || "");
                          setFormData(prev => ({
                            ...prev,
                            customer_id: acceptance.customer_id || "",
                            customer_name: acceptance.customer_name || "",
                            customer_email: acceptance.customer_email || "",
                            customer_phone: acceptance.customer_phone || "",
                            customer_address: acceptance.customer_address || "",
                            customer_fiscal_code: acceptance.customer_fiscal_code || "",
                            vehicle_id: acceptance.vehicle_id || "",
                            vehicle_plate: acceptance.vehicle_plate || "",
                            vehicle_brand: acceptance.vehicle_brand || "",
                            vehicle_model: acceptance.vehicle_model || "",
                            vehicle_version: acceptance.vehicle_version || "",
                            vehicle_year: acceptance.vehicle_year || "",
                            vehicle_vin: acceptance.vehicle_vin || "",
                            vehicle_engine_code: acceptance.vehicle_engine_code || "",
                            vehicle_km: acceptance.vehicle_km || "",
                            vehicle_fuel_type: acceptance.vehicle_fuel_type || "",
                            vehicle_cubic_capacity: acceptance.vehicle_cubic_capacity || "",
                            vehicle_power_kw: acceptance.vehicle_power_kw || "",
                            vehicle_power_hp: acceptance.vehicle_power_hp || "",
                            vehicle_first_registration: acceptance.vehicle_first_registration || ""
                          }));
                          toast.success("Dati importati dall'accettazione");
                        }
                      }
                    }}
                  >
                    <option value="">-- Seleziona accettazione --</option>
                    {acceptances.map(a => (
                      <option key={a.acceptance_id} value={a.acceptance_id}>
                        #{a.acceptance_number} - {a.vehicle_plate} ({a.customer_name})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Customer Selection */}
              {customers.length > 0 && (
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-primary" />
                    Seleziona da Anagrafica
                  </label>
                  <select
                    className="form-input"
                    value={selectedCustomerId}
                    onChange={(e) => {
                      setSelectedCustomerId(e.target.value);
                      handleCustomerSelect(e.target.value);
                    }}
                  >
                    <option value="">-- Seleziona cliente esistente --</option>
                    {customers.map(c => (
                      <option key={c.customer_id} value={c.customer_id}>
                        {c.name} - {c.fiscal_code || c.vat_number} {c.customer_type ? `(${CUSTOMER_TYPES[c.customer_type]?.label || c.customer_type})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Customer Data */}
              {/* Tipo Cliente */}
              <div className="form-group">
                <label className="form-label">Tipo Cliente <span className="text-red-400">*</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(CUSTOMER_TYPES).map(([type, config]) => (
                    <button
                      key={type}
                      type="button"
                      data-testid={`customer-type-${type.toLowerCase()}`}
                      onClick={() => setFormData(prev => ({
                        ...prev, 
                        customer_type: type,
                        split_payment: type === "PA" ? true : prev.split_payment
                      }))}
                      className={`p-2 rounded border-2 transition-all text-sm ${
                        formData.customer_type === type 
                          ? `${config.bgColor} border-current ${config.color}` 
                          : !formData.customer_type
                            ? 'border-red-500/40 text-zinc-400 hover:border-zinc-600'
                            : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'
                      }`}
                    >
                      {config.label}
                    </button>
                  ))}
                </div>
                {!formData.customer_type && (
                  <p className="text-red-400 text-xs mt-1">Seleziona il tipo di cliente</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">
                    {formData.customer_type === "B2C" ? "Nome e Cognome" : formData.customer_type ? "Ragione Sociale" : "Nome / Ragione Sociale"} *
                  </label>
                  <input
                    data-testid="input-quote-customer-name"
                    type="text"
                    className="form-input"
                    value={formData.customer_name}
                    onChange={(e) => setFormData(prev => ({...prev, customer_name: e.target.value}))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Codice Fiscale {formData.customer_type === "B2C" && "*"}
                  </label>
                  <input
                    type="text"
                    className="form-input uppercase"
                    value={formData.customer_fiscal_code}
                    onChange={(e) => setFormData(prev => ({...prev, customer_fiscal_code: e.target.value.toUpperCase()}))}
                    required={formData.customer_type === "B2C"}
                  />
                </div>
                
                {/* P.IVA - obbligatoria per B2B e PA */}
                {(formData.customer_type === "B2B" || formData.customer_type === "PA") && (
                  <div className="form-group">
                    <label className="form-label">Partita IVA *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.customer_vat_number}
                      onChange={(e) => setFormData(prev => ({...prev, customer_vat_number: e.target.value.replace(/\D/g, '').slice(0, 11)}))}
                      placeholder="12345678901"
                      maxLength={11}
                      required
                    />
                  </div>
                )}
                
                {/* SDI/IPA */}
                {(formData.customer_type === "B2B" || formData.customer_type === "PA") && (
                  <div className="form-group">
                    <label className="form-label">
                      {formData.customer_type === "PA" ? "Codice IPA (6 car.) *" : "Codice SDI (7 car.)"}
                    </label>
                    <input
                      type="text"
                      className="form-input uppercase"
                      value={formData.customer_sdi_code}
                      onChange={(e) => setFormData(prev => ({...prev, customer_sdi_code: e.target.value.toUpperCase().slice(0, formData.customer_type === "PA" ? 6 : 7)}))}
                      placeholder={formData.customer_type === "PA" ? "UFXXXX" : "0000000"}
                      maxLength={formData.customer_type === "PA" ? 6 : 7}
                      required={formData.customer_type === "PA"}
                    />
                  </div>
                )}
                
                {/* PEC - per B2B se SDI non specificato */}
                {formData.customer_type === "B2B" && (
                  <div className="form-group">
                    <label className="form-label">
                      PEC {(!formData.customer_sdi_code || formData.customer_sdi_code === "0000000") && "*"}
                    </label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.customer_pec}
                      onChange={(e) => setFormData(prev => ({...prev, customer_pec: e.target.value}))}
                      placeholder="esempio@pec.it"
                      required={!formData.customer_sdi_code || formData.customer_sdi_code === "0000000"}
                    />
                  </div>
                )}
                
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.customer_email}
                    onChange={(e) => setFormData(prev => ({...prev, customer_email: e.target.value}))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Telefono *</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData(prev => ({...prev, customer_phone: e.target.value}))}
                    required
                  />
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Indirizzo (Via/Piazza e numero) *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.customer_address}
                    onChange={(e) => setFormData(prev => ({...prev, customer_address: e.target.value}))}
                    placeholder="Es: Via Roma 123"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Città *</label>
                  <CityAutocomplete
                    value={formData.customer_city}
                    onChange={(city) => setFormData(prev => ({...prev, customer_city: city}))}
                    onProvinceChange={(province) => setFormData(prev => ({...prev, customer_province: province}))}
                    onCapChange={(cap) => setFormData(prev => ({...prev, customer_cap: cap}))}
                    placeholder="Cerca città..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Provincia *</label>
                  <select
                    className="form-select"
                    value={formData.customer_province}
                    onChange={(e) => setFormData(prev => ({...prev, customer_province: e.target.value}))}
                    required
                  >
                    <option value="">Seleziona...</option>
                    {PROVINCE_ITALIANE.map(prov => (
                      <option key={prov} value={prov}>{prov}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">CAP *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.customer_cap}
                    onChange={(e) => setFormData(prev => ({...prev, customer_cap: e.target.value.replace(/\D/g, '').slice(0, 5)}))}
                    placeholder="Es: 20100"
                    maxLength={5}
                    required
                  />
                </div>
                
                {/* Campi PA: CIG e CUP */}
                {formData.customer_type === "PA" && (
                  <>
                    <div className="col-span-2 border-t border-amber-500/30 pt-3 mt-2">
                      <p className="text-amber-400 text-sm font-medium mb-3">Dati Pubblica Amministrazione</p>
                    </div>
                    <div className="form-group">
                      <label className="form-label text-amber-400">CIG (Codice Identificativo Gara) *</label>
                      <input
                        type="text"
                        className="form-input uppercase border-amber-500/30 focus:border-amber-500"
                        value={formData.cig}
                        onChange={(e) => setFormData(prev => ({...prev, cig: e.target.value.toUpperCase()}))}
                        placeholder="Z1234567AB"
                        maxLength={10}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label text-amber-400">CUP (Codice Unico Progetto)</label>
                      <input
                        type="text"
                        className="form-input uppercase border-amber-500/30 focus:border-amber-500"
                        value={formData.cup}
                        onChange={(e) => setFormData(prev => ({...prev, cup: e.target.value.toUpperCase()}))}
                        placeholder="J11B12000000001"
                        maxLength={15}
                      />
                    </div>
                    <div className="form-group col-span-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.split_payment}
                          onChange={(e) => setFormData(prev => ({...prev, split_payment: e.target.checked}))}
                          className="form-checkbox"
                          disabled
                        />
                        <span className="text-sm text-amber-400">Split Payment (IVA versata dalla PA)</span>
                      </label>
                      <p className="text-xs text-zinc-500 mt-1 ml-6">Attivo automaticamente per la Pubblica Amministrazione</p>
                    </div>
                  </>
                )}
              </div>
                
              {/* Vehicle Selection with Customer Vehicle Archive */}
              <CustomerVehicleSelector
                customerId={selectedCustomerId}
                vehicleData={{
                  vehicle_id: formData.vehicle_id,
                  vehicle_plate: formData.vehicle_plate,
                  vehicle_brand: formData.vehicle_brand,
                  vehicle_model: formData.vehicle_model,
                  vehicle_version: formData.vehicle_version,
                  vehicle_year: formData.vehicle_year,
                  vehicle_vin: formData.vehicle_vin,
                  vehicle_engine_code: formData.vehicle_engine_code,
                  vehicle_km: formData.vehicle_km,
                  vehicle_fuel_type: formData.vehicle_fuel_type,
                  vehicle_cubic_capacity: formData.vehicle_cubic_capacity,
                  vehicle_power_kw: formData.vehicle_power_kw,
                  vehicle_power_hp: formData.vehicle_power_hp,
                  vehicle_first_registration: formData.vehicle_first_registration
                }}
                onVehicleChange={(updater) => {
                  if (typeof updater === 'function') {
                    setFormData(prev => ({...prev, ...updater(prev)}));
                  } else {
                    setFormData(prev => ({...prev, ...updater}));
                  }
                }}
                required
              />

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-primary uppercase text-sm font-bold flex items-center gap-2">
                    <Barcode className="w-4 h-4" />
                    Articoli
                    {totalSuggestedHours > 0 && (
                      <span className="text-xs text-blue-400 font-normal flex items-center gap-1 ml-2">
                        <Clock className="w-3 h-3" />
                        {totalSuggestedHours}h suggerite
                      </span>
                    )}
                  </h3>
                  <div className="flex gap-2">
                    {Object.entries(LINE_TYPES).slice(0, 3).map(([type, config]) => (
                      <Button 
                        key={type}
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addItem(type)} 
                        className={`border-zinc-700 ${config.color} hover:bg-zinc-800`}
                      >
                        <span className="mr-1">{config.icon}</span> {config.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  {formData.items.map((item, index) => {
                    const lineConfig = LINE_TYPES[item.line_type] || LINE_TYPES.RICAMBIO;
                    const isManodopera = item.line_type === "MANODOPERA";
                    
                    return (
                    <div 
                      key={index} 
                      className={`p-3 rounded-lg border ${
                        isManodopera 
                          ? "bg-blue-900/20 border-blue-500/30" 
                          : item.line_type === "SPESE_ACCESSORIE"
                            ? "bg-gray-800/30 border-gray-600/30"
                            : item.line_type === "SERVIZIO"
                              ? "bg-purple-900/20 border-purple-500/30"
                              : "bg-zinc-950/50 border-zinc-800"
                      }`}
                    >
                      {/* Tipo riga e controlli */}
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <select
                            className={`form-select text-xs py-1 px-2 ${lineConfig.color} bg-zinc-900 border-zinc-700`}
                            value={item.line_type || "RICAMBIO"}
                            onChange={(e) => updateItem(index, "line_type", e.target.value)}
                          >
                            {Object.entries(LINE_TYPES).map(([type, cfg]) => (
                              <option key={type} value={type}>{cfg.icon} {cfg.label}</option>
                            ))}
                          </select>
                          {item.vat_rate === "0" && (
                            <select
                              className="form-select text-xs py-1 px-2 text-amber-400 bg-zinc-900 border-amber-500/30"
                              value={item.natura || ""}
                              onChange={(e) => updateItem(index, "natura", e.target.value)}
                              required
                            >
                              {NATURE_IVA.map(n => (
                                <option key={n.code} value={n.code}>{n.label}</option>
                              ))}
                            </select>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-400 hover:text-red-300 p-1"
                          disabled={formData.items.length === 1}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Campi riga */}
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-2 form-group">
                          <label className="form-label text-xs">Codice</label>
                          <input
                            type="text"
                            className="form-input uppercase text-xs"
                            value={item.item_code || ""}
                            onChange={(e) => updateItem(index, "item_code", e.target.value.toUpperCase())}
                            placeholder="COD"
                          />
                        </div>
                        <div className="col-span-4 form-group">
                          <label className="form-label text-xs flex items-center gap-1">
                            Descrizione
                            {suggestedLaborHours[index] > 0 && (
                              <span className="text-blue-400 text-[10px]">({suggestedLaborHours[index]}h)</span>
                            )}
                          </label>
                          <TemparioSelector
                            value={item.description || ""}
                            onChange={(val) => updateItem(index, "description", val)}
                            onSelectItem={(catalogItem) => updateItemFromCatalog(index, catalogItem)}
                            placeholder="Cerca articolo..."
                            className="w-full"
                          />
                        </div>
                        
                        <div className="col-span-2 form-group">
                          <label className="form-label text-xs">Marca/Forn.</label>
                          <input
                            type="text"
                            className="form-input text-xs"
                            value={item.brand || ""}
                            onChange={(e) => updateItem(index, "brand", e.target.value)}
                            placeholder="Marca"
                          />
                        </div>
                        
                        <div className="col-span-1 form-group">
                          <label className="form-label text-xs">Qtà</label>
                          <input
                            type="number"
                            step="any"
                            className="form-input text-xs"
                            value={item.quantity || ""}
                            onChange={(e) => updateItem(index, "quantity", e.target.value)}
                            min="0"
                            placeholder="1"
                          />
                        </div>
                        <div className="col-span-1 form-group">
                          <label className="form-label text-xs">Prezzo</label>
                          <input
                            type="number"
                            step="0.01"
                            className="form-input text-xs"
                            value={item.unit_price || ""}
                            onChange={(e) => updateItem(index, "unit_price", e.target.value)}
                            min="0"
                            placeholder="0.00"
                          />
                        </div>
                        
                        {/* Campo Costo Acquisto per calcolo margine (solo ricambi) */}
                        {(item.line_type || item.type) !== "MANODOPERA" && (
                          <div className="col-span-1 form-group">
                            <label className="form-label text-xs text-green-400">Costo Acq.</label>
                            <input
                              type="number"
                              step="0.01"
                              className="form-input text-xs border-green-500/30"
                              value={item.cost_price || ""}
                              onChange={(e) => updateItem(index, "cost_price", e.target.value)}
                              min="0"
                              placeholder="0.00"
                              title="Costo acquisto per calcolo margine"
                            />
                          </div>
                        )}
                        
                        <div className="col-span-1 form-group">
                          <label className="form-label text-xs">IVA%</label>
                          <select
                            className={`form-select text-xs ${item.vat_rate === "0" ? "text-amber-400 border-amber-500/30" : ""}`}
                            value={item.vat_rate || "22"}
                            onChange={(e) => updateItem(index, "vat_rate", e.target.value)}
                          >
                            <option value="22">22%</option>
                            <option value="10">10%</option>
                            <option value="4">4%</option>
                            <option value="0">0%</option>
                          </select>
                        </div>
                        
                        <div className="col-span-1 form-group">
                          <label className="form-label text-xs">Sc.%</label>
                          <input
                            type="number"
                            step="0.1"
                            className="form-input text-xs"
                            value={item.discount_percent || ""}
                            onChange={(e) => updateItem(index, "discount_percent", e.target.value)}
                            min="0"
                            max="100"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                  )})}
                </div>
              </div>

              {/* Labor - ora è informativo perché le ore sono negli items */}
              <div className="grid grid-cols-3 gap-4 bg-blue-900/10 border border-blue-500/20 p-4 rounded-sm">
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    Costo Manodopera (€/h)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input"
                    value={formData.labor_cost}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      setFormData({...formData, labor_cost: value});
                    }}
                    placeholder="40.00"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    Ore Manodopera
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input"
                    value={formData.labor_hours}
                    onChange={(e) => {
                      // Accetta sia punto che virgola come separatore decimale
                      const value = e.target.value.replace(',', '.');
                      setFormData({...formData, labor_hours: value});
                    }}
                    placeholder="0"
                  />
                  <p className="text-zinc-500 text-xs mt-1">
                    Subtotale: € {((parseFloat(String(formData.labor_cost || "").replace(',', '.')) || 0) * (parseFloat(String(formData.labor_hours || "").replace(',', '.')) || 0)).toFixed(2)}
                  </p>
                </div>
                <div className="form-group">
                  <label className="form-label">Validità (giorni)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.validity_days}
                    onChange={(e) => setFormData({...formData, validity_days: e.target.value})}
                    placeholder="30"
                  />
                </div>
              </div>

              {/* Additional Costs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Smaltimento Rifiuti (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.waste_disposal_cost}
                    onChange={(e) => setFormData({...formData, waste_disposal_cost: e.target.value})}
                    placeholder="0.00"
                    data-testid="input-waste-disposal"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Costo forfettario smaltimento olii, filtri, ecc.</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Materiali di Consumo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={formData.consumables_cost}
                    onChange={(e) => setFormData({...formData, consumables_cost: e.target.value})}
                    placeholder="0.00"
                    data-testid="input-consumables"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Costo forfettario materiali di consumo</p>
                </div>
              </div>

              {/* Payment Method */}
              <div className="form-group">
                <label className="form-label">Modalità Pagamento (Fattura Elettronica)</label>
                <select
                  className="form-select"
                  value={formData.payment_method}
                  onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                  data-testid="select-payment-method"
                >
                  {paymentMethods.map(pm => (
                    <option key={pm.code} value={pm.code}>
                      {pm.code} - {pm.label}
                    </option>
                  ))}
                </select>
                <p className="text-zinc-500 text-xs mt-1">Questa modalità verrà utilizzata nella fattura elettronica XML</p>
              </div>

              {/* Notes */}
              <div className="form-group">
                <label className="form-label">Note</label>
                <textarea
                  className="form-textarea h-20"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note aggiuntive..."
                />
              </div>

              {/* Totals */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <div className="text-xs text-zinc-500 mb-3 uppercase tracking-wider">Riepilogo IVA</div>
                
                {/* Dettaglio per aliquota */}
                {(() => {
                  // Calcola riepilogo per aliquota
                  const vatSummary = {};
                  formData.items.forEach(item => {
                    const qty = parseFloat(String(item.quantity || "0").replace(',', '.')) || 0;
                    const price = parseFloat(String(item.unit_price || "0").replace(',', '.')) || 0;
                    const vatRate = parseFloat(String(item.vat_rate || "22").replace(',', '.')) || 22;
                    const itemSubtotal = Math.round(qty * price * 100) / 100;
                    
                    if (!vatSummary[vatRate]) {
                      vatSummary[vatRate] = { imponibile: 0, imposta: 0 };
                    }
                    vatSummary[vatRate].imponibile += itemSubtotal;
                    vatSummary[vatRate].imposta += Math.round(itemSubtotal * vatRate / 100 * 100) / 100;
                  });
                  
                  // Aggiungi manodopera (22%) se non ci sono righe MANODOPERA
                  const hasLaborRows = formData.items.some(i => i.is_labor || i.line_type === "MANODOPERA");
                  if (!hasLaborRows) {
                    const laborCost = parseFloat(String(formData.labor_cost || "0").replace(',', '.')) || 0;
                    const laborHours = parseFloat(String(formData.labor_hours || "0").replace(',', '.')) || 0;
                    const laborSubtotal = Math.round(laborCost * laborHours * 100) / 100;
                    if (laborSubtotal > 0) {
                      if (!vatSummary[22]) vatSummary[22] = { imponibile: 0, imposta: 0 };
                      vatSummary[22].imponibile += laborSubtotal;
                      vatSummary[22].imposta += Math.round(laborSubtotal * 0.22 * 100) / 100;
                    }
                  }
                  
                  // Aggiungi costi accessori (22%)
                  const waste = parseFloat(String(formData.waste_disposal_cost || "0").replace(',', '.')) || 0;
                  const cons = parseFloat(String(formData.consumables_cost || "0").replace(',', '.')) || 0;
                  const accessori = Math.round((waste + cons) * 100) / 100;
                  if (accessori > 0) {
                    if (!vatSummary[22]) vatSummary[22] = { imponibile: 0, imposta: 0 };
                    vatSummary[22].imponibile += accessori;
                    vatSummary[22].imposta += Math.round(accessori * 0.22 * 100) / 100;
                  }
                  
                  return Object.entries(vatSummary)
                    .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                    .map(([rate, data]) => (
                      <div key={rate} className="flex justify-between text-xs text-zinc-400 mb-1">
                        <span>Aliquota {rate}%:</span>
                        <span className="font-['JetBrains_Mono']">
                          Imp. €{data.imponibile.toFixed(2)} → IVA €{data.imposta.toFixed(2)}
                        </span>
                      </div>
                    ));
                })()}
                
                <div className="border-t border-zinc-800 mt-3 pt-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Ricambi:</span>
                    <span className="text-white font-['JetBrains_Mono']">€ {totals.ricambiSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Manodopera:</span>
                    <span className="text-white font-['JetBrains_Mono']">€ {totals.manodoperaSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">IVA:</span>
                    <span className="text-white font-['JetBrains_Mono']">€ {totals.vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg pt-2 border-t border-dashed border-zinc-700">
                    <span className="text-white font-bold">Totale:</span>
                    <span className="text-primary font-bold font-['JetBrains_Mono']">€ {totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowDialog(false)} className="text-zinc-400">
                  Annulla
                </Button>
                <Button data-testid="submit-quote-btn" type="submit" className="btn-primary">
                  {editingQuote ? "Salva Modifiche" : "Crea Preventivo"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Cerca per numero, cliente, targa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="search-quotes"
        />
      </div>

      {/* Quotes List - Table View */}
      {filteredQuotes.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-xs text-zinc-500 uppercase">
                <th className="py-3 px-4 font-medium">N°</th>
                <th className="py-3 px-4 font-medium">Stato</th>
                <th className="py-3 px-4 font-medium">Cliente</th>
                <th className="py-3 px-4 font-medium">Veicolo</th>
                <th className="py-3 px-4 font-medium">Targa</th>
                <th className="py-3 px-4 font-medium text-right">Totale</th>
                <th className="py-3 px-4 font-medium text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotes.map((quote) => {
                const linkedInvoice = getLinkedInvoice(quote.quote_number);
                return (
                  <tr 
                    key={quote.quote_id}
                    className="border-b border-zinc-800/50 hover:bg-zinc-900/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedQuote(quote)}
                  >
                    <td className="py-3 px-4">
                      <span className="text-primary font-bold">#{quote.quote_number}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs uppercase ${statusColors[quote.status]}`}>
                          {statusLabels[quote.status]}
                        </span>
                        {linkedInvoice && (
                          <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                            <Link className="w-3 h-3" />
                            #{linkedInvoice}
                          </span>
                        )}
                        {quote.viewed_at && (
                          <span
                            title={`Visualizzato dal cliente il ${new Date(quote.last_viewed_at || quote.viewed_at).toLocaleString("it-IT")}`}
                            className="px-1.5 py-0.5 rounded text-xs bg-blue-500/20 text-blue-400 flex items-center gap-1"
                            data-testid={`quote-viewed-badge-${quote.quote_number}`}
                          >
                            <Eye className="w-3 h-3" />
                            Visto
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-white font-medium">{quote.customer_name}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-zinc-400">{quote.vehicle_model || '-'}</span>
                      {quote.vehicle_km > 0 && (
                        <span className="text-zinc-600 text-xs ml-2">
                          ({(quote.vehicle_km).toLocaleString()} km)
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-zinc-300 font-mono">{quote.vehicle_plate || '-'}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-white font-bold font-mono">€ {(quote.total || 0).toFixed(2)}</span>
                    </td>
                    <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(quote)}
                          className="h-8 w-8 p-0 text-zinc-400 hover:text-primary"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(quote.quote_id)}
                          className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <FileText className="w-16 h-16 empty-state-icon" />
          <p className="empty-state-title">{searchTerm ? "Nessun risultato" : "Nessun preventivo"}</p>
          <p className="empty-state-text">{searchTerm ? "Prova con altri termini" : "Crea il tuo primo preventivo"}</p>
        </div>
      )}

      {/* Detail Dialog */}
      {selectedQuote && (
        <Dialog open={!!selectedQuote} onOpenChange={() => setSelectedQuote(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] text-xl">
                Preventivo #{selectedQuote.quote_number}
              </DialogTitle>
            </DialogHeader>
            
            {/* Delete Confirmation */}
            {deleteConfirm && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4 mb-4">
                <p className="text-red-400 mb-3">Confermi l'eliminazione di questo preventivo? I numeri saranno riassegnati automaticamente.</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleDeleteQuote(deleteConfirm)}
                    size="sm"
                    className="bg-red-500 hover:bg-red-600"
                  >
                    Conferma Eliminazione
                  </Button>
                  <Button
                    onClick={() => setDeleteConfirm(null)}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700"
                  >
                    Annulla
                  </Button>
                </div>
              </div>
            )}
            
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Select
                  value={selectedQuote.status}
                  onValueChange={(value) => handleStatusChange(selectedQuote.quote_id, value)}
                >
                  <SelectTrigger className="w-40 bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="draft">Bozza</SelectItem>
                    <SelectItem value="sent">Inviato</SelectItem>
                    <SelectItem value="accepted">Accettato</SelectItem>
                    <SelectItem value="rejected">Rifiutato</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => sendViaEmail(selectedQuote)}
                    className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                    data-testid="send-email-btn"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Email
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => sendViaWhatsApp(selectedQuote)}
                    className="border-green-500 text-green-500 hover:bg-green-500/10"
                    data-testid="send-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => downloadPDF(selectedQuote)}
                    className="border-primary text-primary hover:bg-primary/10"
                    data-testid="export-pdf-btn"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    PDF
                  </Button>
                  <Button
                    onClick={() => openShareDialog(selectedQuote)}
                    disabled={sharing}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="share-pdf-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {sharing ? "Generazione..." : "Invia con PDF"}
                  </Button>
                  {selectedQuote.status === "accepted" && (
                    <Button
                      onClick={() => validateAndCreateInvoice(selectedQuote.quote_id)}
                      className="btn-primary"
                      data-testid="create-invoice-btn"
                    >
                      <Receipt className="w-5 h-5 mr-2" />
                      Crea Fattura
                    </Button>
                  )}
                </div>
              </div>

              {/* Indicatore visualizzazione cliente */}
              <div
                data-testid="quote-view-status"
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border ${
                  selectedQuote.viewed_at
                    ? "bg-green-500/10 border-green-500/30 text-green-400"
                    : "bg-zinc-800/40 border-zinc-700 text-zinc-400"
                }`}
              >
                {selectedQuote.viewed_at ? (
                  <>
                    <Eye className="w-4 h-4 shrink-0" />
                    <span>
                      Visualizzato dal cliente il{" "}
                      <strong>{new Date(selectedQuote.last_viewed_at || selectedQuote.viewed_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</strong>
                      {selectedQuote.view_count > 1 && ` · ${selectedQuote.view_count} aperture`}
                    </span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 shrink-0" />
                    <span>Non ancora visualizzato dal cliente</span>
                  </>
                )}
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-4 text-sm" data-testid="quote-detail-vehicle-info">
                <div>
                  <span className="text-zinc-500">Cliente:</span>
                  <span className="text-white ml-2">{selectedQuote.customer_name}</span>
                </div>
                <div>
                  <span className="text-zinc-500">CF:</span>
                  <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedQuote.customer_fiscal_code}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Veicolo:</span>
                  <span className="text-white ml-2">
                    {(() => {
                      const b = (selectedQuote.vehicle_brand || "").trim();
                      const m = (selectedQuote.vehicle_model || "").trim();
                      let name = (b && m && m.toLowerCase().startsWith(b.toLowerCase())) ? m : `${b} ${m}`.trim();
                      if (selectedQuote.vehicle_version) name = `${name} ${selectedQuote.vehicle_version}`.trim();
                      return name || "-";
                    })()}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">Targa:</span>
                  <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedQuote.vehicle_plate}</span>
                </div>
                {selectedQuote.vehicle_year > 0 && (
                  <div>
                    <span className="text-zinc-500">Anno:</span>
                    <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedQuote.vehicle_year}</span>
                  </div>
                )}
                {selectedQuote.vehicle_fuel_type && (
                  <div>
                    <span className="text-zinc-500">Alimentazione:</span>
                    <span className="text-white ml-2">{selectedQuote.vehicle_fuel_type}</span>
                  </div>
                )}
                {selectedQuote.vehicle_engine_code && (
                  <div>
                    <span className="text-zinc-500">Codice motore:</span>
                    <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedQuote.vehicle_engine_code}</span>
                  </div>
                )}
                {selectedQuote.vehicle_vin && (
                  <div>
                    <span className="text-zinc-500">Telaio (VIN):</span>
                    <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedQuote.vehicle_vin}</span>
                  </div>
                )}
                {selectedQuote.vehicle_km > 0 && (
                  <div className="col-span-2">
                    <span className="text-zinc-500 inline-flex items-center gap-1">
                      <Gauge className="w-4 h-4" />
                      Kilometri:
                    </span>
                    <span className="text-white ml-2 font-['JetBrains_Mono']">{selectedQuote.vehicle_km.toLocaleString()} km</span>
                  </div>
                )}
              </div>

              {/* Items Table */}
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th>Descrizione</th>
                      <th>Marca</th>
                      <th className="text-right">Qtà</th>
                      <th className="text-right">Prezzo</th>
                      <th className="text-right">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedQuote.items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.description}</td>
                        <td>{item.brand || "-"}</td>
                        <td className="text-right font-['JetBrains_Mono']">{item.quantity}</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(item.unit_price || 0).toFixed(2)}</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(item.subtotal || (item.quantity * item.unit_price * 1.22) || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {/* Mostra riga manodopera dal campo SOLO se NON ci sono righe MANODOPERA */}
                    {selectedQuote.labor_hours > 0 && 
                     !(selectedQuote.items || []).some(i => i.is_labor || i.line_type === "MANODOPERA") && (
                      <tr>
                        <td>Manodopera</td>
                        <td>-</td>
                        <td className="text-right font-['JetBrains_Mono']">{selectedQuote.labor_hours}h</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(selectedQuote.labor_cost || 0).toFixed(2)}/h</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {((selectedQuote.labor_cost || 0) * (selectedQuote.labor_hours || 0)).toFixed(2)}</td>
                      </tr>
                    )}
                    {selectedQuote.waste_disposal_cost > 0 && (
                      <tr>
                        <td>Smaltimento Rifiuti</td>
                        <td>-</td>
                        <td className="text-right font-['JetBrains_Mono']">1</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(selectedQuote.waste_disposal_cost || 0).toFixed(2)}</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(selectedQuote.waste_disposal_cost || 0).toFixed(2)}</td>
                      </tr>
                    )}
                    {selectedQuote.consumables_cost > 0 && (
                      <tr>
                        <td>Materiali di Consumo</td>
                        <td>-</td>
                        <td className="text-right font-['JetBrains_Mono']">1</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(selectedQuote.consumables_cost || 0).toFixed(2)}</td>
                        <td className="text-right font-['JetBrains_Mono']">€ {(selectedQuote.consumables_cost || 0).toFixed(2)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <div className="text-xs text-zinc-500 mb-3 uppercase tracking-wider">Riepilogo IVA</div>
                
                {/* Dettaglio per aliquota dal preventivo salvato */}
                {(() => {
                  const vatSummary = {};
                  (selectedQuote.items || []).forEach(item => {
                    const qty = parseFloat(item.quantity || 0);
                    const price = parseFloat(item.unit_price || 0);
                    const vatRate = parseFloat(item.vat_rate || 22);
                    const itemSubtotal = Math.round(qty * price * 100) / 100;
                    
                    if (!vatSummary[vatRate]) {
                      vatSummary[vatRate] = { imponibile: 0, imposta: 0 };
                    }
                    vatSummary[vatRate].imponibile += itemSubtotal;
                    vatSummary[vatRate].imposta += Math.round(itemSubtotal * vatRate / 100 * 100) / 100;
                  });
                  
                  // Manodopera dal campo (22%) se non ci sono righe MANODOPERA
                  const hasLaborRows = (selectedQuote.items || []).some(i => i.is_labor || i.line_type === "MANODOPERA");
                  if (!hasLaborRows) {
                    const laborSubtotal = (selectedQuote.labor_cost || 0) * (selectedQuote.labor_hours || 0);
                    if (laborSubtotal > 0) {
                      if (!vatSummary[22]) vatSummary[22] = { imponibile: 0, imposta: 0 };
                      vatSummary[22].imponibile += laborSubtotal;
                      vatSummary[22].imposta += Math.round(laborSubtotal * 0.22 * 100) / 100;
                    }
                  }
                  
                  // Costi accessori (22%)
                  const accessori = (selectedQuote.waste_disposal_cost || 0) + (selectedQuote.consumables_cost || 0);
                  if (accessori > 0) {
                    if (!vatSummary[22]) vatSummary[22] = { imponibile: 0, imposta: 0 };
                    vatSummary[22].imponibile += accessori;
                    vatSummary[22].imposta += Math.round(accessori * 0.22 * 100) / 100;
                  }
                  
                  return Object.entries(vatSummary)
                    .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                    .map(([rate, data]) => (
                      <div key={rate} className="flex justify-between text-xs text-zinc-400 mb-1">
                        <span>Aliquota {rate}%:</span>
                        <span className="font-['JetBrains_Mono']">
                          Imp. €{data.imponibile.toFixed(2)} → IVA €{data.imposta.toFixed(2)}
                        </span>
                      </div>
                    ));
                })()}
                
                <div className="border-t border-zinc-800 mt-3 pt-3 space-y-2">
                  {/* Calcola ricambi e manodopera dal preventivo */}
                  {(() => {
                    let ricambiTotal = 0;
                    let manodoperaTotal = 0;
                    
                    // Calcola dai items
                    (selectedQuote.items || []).forEach(item => {
                      const itemTotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
                      if (item.is_labor || item.line_type === "MANODOPERA") {
                        manodoperaTotal += itemTotal;
                      } else {
                        ricambiTotal += itemTotal;
                      }
                    });
                    
                    // Aggiungi manodopera dal campo labor se non ci sono righe manodopera
                    if (manodoperaTotal === 0) {
                      manodoperaTotal = (selectedQuote.labor_cost || 0) * (selectedQuote.labor_hours || 0);
                    }
                    
                    // Aggiungi costi accessori ai ricambi
                    ricambiTotal += (selectedQuote.waste_disposal_cost || 0) + (selectedQuote.consumables_cost || 0);
                    
                    return (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-500">Ricambi:</span>
                          <span className="text-white font-['JetBrains_Mono']">€ {ricambiTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-zinc-500">Manodopera:</span>
                          <span className="text-white font-['JetBrains_Mono']">€ {manodoperaTotal.toFixed(2)}</span>
                        </div>
                      </>
                    );
                  })()}
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">IVA:</span>
                    <span className="text-white font-['JetBrains_Mono']">€ {(selectedQuote.vat_total || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl pt-2 border-t border-dashed border-zinc-700">
                    <span className="text-white font-bold">Totale:</span>
                    <span className="text-primary font-bold font-['JetBrains_Mono']">€ {(selectedQuote.total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {selectedQuote.points_earned > 0 && (
                <div className="text-center text-primary">
                  +{selectedQuote.points_earned} punti guadagnati con questo preventivo
                </div>
              )}

              {/* Azioni Modifica / Elimina in basso a destra */}
              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button
                  onClick={() => {
                    setSelectedQuote(null);
                    openEditDialog(selectedQuote);
                  }}
                  variant="outline"
                  size="sm"
                  data-testid="edit-quote-btn"
                  className="border-primary/50 text-primary hover:bg-primary/10"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifica
                </Button>
                <Button
                  onClick={() => setDeleteConfirm(selectedQuote.quote_id)}
                  variant="outline"
                  size="sm"
                  data-testid="delete-quote-btn"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Elimina
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Share Quote Dialog (public PDF link) */}
      {shareData && (
        <Dialog open={!!shareData} onOpenChange={(open) => { if (!open) setShareData(null); }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg" data-testid="share-quote-dialog">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                <Send className="w-5 h-5 text-green-500" />
                Invia Preventivo #{shareData.quote.quote_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-zinc-400">
                Il cliente potrà visualizzare e scaricare il PDF del preventivo da questo link:
              </p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={shareData.url}
                  data-testid="share-link-input"
                  className="bg-zinc-950 border-zinc-800 text-zinc-300 font-mono text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyShareLink}
                  data-testid="copy-link-btn"
                  className="border-zinc-700 shrink-0"
                >
                  {linkCopied ? "Copiato!" : "Copia"}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Button
                  onClick={shareViaWhatsApp}
                  data-testid="share-whatsapp-btn"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  onClick={shareViaEmail}
                  data-testid="share-email-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email
                </Button>
              </div>
              <p className="text-xs text-zinc-500 text-center">
                Il messaggio viene precompilato con il link al PDF, pronto da inviare.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Parts Request Configuration Dialog */}
      {showPartsRequestDialog && newQuoteData && (
        <Dialog open={showPartsRequestDialog} onOpenChange={(open) => {
          if (!open) {
            setShowPartsRequestDialog(false);
            setShowDialog(true); // Torna al form preventivo
          }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" />
                Richiesta Ricambi
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="bg-zinc-950 p-3 rounded border border-zinc-800">
                <p className="text-zinc-400 text-sm mb-2">
                  Vuoi creare automaticamente una richiesta ricambi per questo preventivo?
                </p>
                <p className="text-white font-semibold">
                  {newQuoteData.items?.length || 0} articoli - {newQuoteData.vehicle_brand} {newQuoteData.vehicle_model}
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Fornitore</label>
                <Select
                  value={partsRequestConfig.supplier_id || "none"}
                  onValueChange={(value) => handleSupplierSelect(value === "none" ? "" : value)}
                >
                  <SelectTrigger className="bg-zinc-950" data-testid="select-supplier">
                    <SelectValue placeholder="Seleziona fornitore (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Nessun fornitore --</SelectItem>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier.supplier_id} value={supplier.supplier_id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {partsRequestConfig.supplier_id && (
                  <div className="mt-2 text-zinc-500 text-xs">
                    {partsRequestConfig.supplier_email && <span className="mr-3">{partsRequestConfig.supplier_email}</span>}
                    {partsRequestConfig.supplier_phone && <span>{partsRequestConfig.supplier_phone}</span>}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Urgenza</label>
                <Select
                  value={partsRequestConfig.urgency}
                  onValueChange={(value) => setPartsRequestConfig(prev => ({ ...prev, urgency: value }))}
                >
                  <SelectTrigger className="bg-zinc-950" data-testid="select-urgency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="morning">Urgente - In Mattinata</SelectItem>
                    <SelectItem value="evening">Urgente - In Serata</SelectItem>
                    <SelectItem value="today">Urgente - Consegna oggi</SelectItem>
                    <SelectItem value="vehicle_down">FERMO MACCHINA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
              <Button
                variant="ghost"
                onClick={() => handlePartsRequestConfirm(false)}
                className="text-zinc-400"
              >
                Salta, crea solo preventivo
              </Button>
              <Button
                onClick={() => handlePartsRequestConfirm(true)}
                className="bg-primary hover:bg-primary/90"
                data-testid="confirm-parts-request"
              >
                <Send className="w-4 h-4 mr-2" />
                Crea Preventivo + Richiesta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Work Order Creation Dialog (on quote acceptance) */}
      <Dialog open={showWorkOrderDialog} onOpenChange={(open) => {
        if (!open) {
          setShowWorkOrderDialog(false);
          setPendingAcceptQuote(null);
        }
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              Crea Ordine di Lavoro
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-zinc-950 p-3 rounded border border-zinc-800">
              <p className="text-zinc-400 text-sm mb-2">
                Il preventivo sta per essere confermato. Seleziona l'urgenza della riparazione per schedulare l'ordine di lavoro.
              </p>
              {pendingAcceptQuote?.quote && (
                <div className="text-white">
                  <p className="font-semibold">
                    Preventivo #{pendingAcceptQuote.quote.quote_number}
                  </p>
                  <p className="text-sm text-zinc-400">
                    {pendingAcceptQuote.quote.vehicle_brand} {pendingAcceptQuote.quote.vehicle_model} - {pendingAcceptQuote.quote.vehicle_plate}
                  </p>
                  <p className="text-sm text-zinc-400">
                    Cliente: {pendingAcceptQuote.quote.customer_name}
                  </p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                Urgenza Riparazione *
              </label>
              <Select
                value={workOrderConfig.urgency}
                onValueChange={(value) => setWorkOrderConfig(prev => ({ ...prev, urgency: value }))}
              >
                <SelectTrigger className="bg-zinc-950" data-testid="select-work-urgency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      Bassa - Oltre 8 giorni
                    </span>
                  </SelectItem>
                  <SelectItem value="medium">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                      Media - Entro 7 giorni
                    </span>
                  </SelectItem>
                  <SelectItem value="high">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      Alta - Entro 3 giorni
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                L'urgenza determina quando verrà schedulato il lavoro. Il ponte verrà assegnato automaticamente.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Operatore (opzionale)</label>
              <Select
                value={workOrderConfig.operator_id || "none"}
                onValueChange={(value) => setWorkOrderConfig(prev => ({ ...prev, operator_id: value === "none" ? "" : value }))}
              >
                <SelectTrigger className="bg-zinc-950">
                  <SelectValue placeholder="Non assegnato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assegnato</SelectItem>
                  {workOrderOperators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.name} {op.specialization && `(${op.specialization})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {workOrderOperators.length === 0 && (
                <p className="text-xs text-zinc-500 mt-1">
                  Nessun operatore configurato. Vai in Configurazioni per aggiungerli.
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Note (opzionale)</label>
              <Input
                value={workOrderConfig.notes}
                onChange={(e) => setWorkOrderConfig(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note per l'ordine di lavoro..."
                className="bg-zinc-950"
              />
            </div>
          </div>

          <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={handleSkipWorkOrder}
              className="text-zinc-400"
              disabled={creatingWorkOrder}
            >
              Salta, solo conferma
            </Button>
            <Button
              onClick={handleCreateWorkOrder}
              className="bg-primary hover:bg-primary/90"
              disabled={creatingWorkOrder}
              data-testid="confirm-work-order"
            >
              {creatingWorkOrder ? (
                <>Creazione...</>
              ) : (
                <>
                  <Wrench className="w-4 h-4 mr-2" />
                  Conferma e Crea Ordine
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Validation Error Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Verifica Dati Fatturazione
            </DialogTitle>
          </DialogHeader>
          
          {invoiceValidation && (
            <div className="space-y-4 mt-4">
              {invoiceValidation.already_invoiced ? (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
                  <p className="text-blue-400">
                    Questo preventivo è già stato fatturato (Fattura #{invoiceValidation.invoice_number}).
                  </p>
                </div>
              ) : invoiceValidation.canProceed ? (
                <>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
                    <p className="text-yellow-400 mb-3 font-semibold">
                      Avvertimenti di formato (non bloccanti):
                    </p>
                    <ul className="space-y-2 text-sm">
                      {invoiceValidation.format_errors?.map((error, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-yellow-300">
                          <span className="text-yellow-500 mt-0.5">•</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-zinc-400 text-sm">
                    Puoi comunque creare la fattura. Questi avvertimenti non impediscono la generazione del documento XML.
                  </p>
                </>
              ) : (
                <>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
                    <p className="text-red-400 mb-3 font-semibold">
                      I seguenti problemi impediscono la creazione della fattura elettronica:
                    </p>
                    <ul className="space-y-2 text-sm">
                      {invoiceValidation.blocking_errors?.map((error, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-red-300">
                          <span className="text-red-500 mt-0.5">•</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {invoiceValidation.format_errors?.length > 0 && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
                      <p className="text-yellow-400 mb-2 text-sm font-semibold">Avvertimenti formato:</p>
                      <ul className="space-y-1 text-sm">
                        {invoiceValidation.format_errors.map((error, idx) => (
                          <li key={idx} className="text-yellow-300 text-xs">• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="text-zinc-400 text-sm">
                    <p>Per correggere questi errori:</p>
                    <ol className="list-decimal ml-4 mt-2 space-y-1">
                      <li>Modifica il preventivo cliccando su "Modifica"</li>
                      <li>Completa i dati mancanti del cliente</li>
                    </ol>
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button 
              variant="ghost" 
              onClick={() => {
                setShowValidationDialog(false);
                setInvoiceValidation(null);
              }}
              className="text-zinc-400"
            >
              Chiudi
            </Button>
            {invoiceValidation?.canProceed && selectedQuote && (
              <Button
                onClick={() => {
                  setShowValidationDialog(false);
                  setInvoiceValidation(null);
                  createInvoice(selectedQuote.quote_id);
                }}
                className="bg-primary hover:bg-primary/80 text-black"
                data-testid="create-invoice-anyway-btn"
              >
                Crea Fattura Comunque
              </Button>
            )}
            {!invoiceValidation?.already_invoiced && !invoiceValidation?.canProceed && selectedQuote && (
              <Button
                onClick={() => {
                  setShowValidationDialog(false);
                  setInvoiceValidation(null);
                  openEditDialog(selectedQuote);
                  setShowDialog(true);
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <Edit className="w-4 h-4 mr-2" />
                Modifica Preventivo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
